import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { invalidateProjectRelatedQueries } from '@/modules/common/utils/query-invalidation';

import { patchProjectChange } from '../api/projects.api';
import { PatchProjectChangeParams } from '../types';
import { PROJECT_BY_REVISION_DATE_QUERY_KEY_ROOT } from './use-project-details-by-revision-date';

interface UsePatchProjectChangeParams {
  onSuccess?: () => void;
}

/**
 * Hook for updating a specific project change in EditChange mode.
 * Uses PATCH /api/project-changes endpoint which requires a changeId.
 *
 * Usage:
 * - Call this hook only in EditChange mode
 * - Provides mutation to update an existing change record
 * - Automatically invalidates project queries after successful update
 */
export const usePatchProjectChange = ({ onSuccess }: UsePatchProjectChangeParams = {}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.projects');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  return useMutation({
    mutationFn: async (params: PatchProjectChangeParams) => {
      await patchProjectChange(params);
      return { changeType: params.changeType, projectId: params.projectId };
    },
    onSuccess: async (_result, variables) => {
      const { projectId } = variables;

      // Invalidate project queries to refetch updated data (includes project-changes)
      await invalidateProjectRelatedQueries(queryClient);

      // Invalidate historical data queries (revision-date based) — a save can
      // shift the snapshot at any previously-fetched revision date for this project.
      await queryClient.invalidateQueries({
        queryKey: [PROJECT_BY_REVISION_DATE_QUERY_KEY_ROOT, Number(projectId)],
      });

      displayToastSuccess(t('details.tabs.changes.updateSuccess'));
      onSuccess?.();
    },
    onError: error => {
      displayToastErrorFromApiWithFallback(error, 'details.tabs.changes.updateError');
    },
  });
};
